'use client'

// MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Skeleton from '@mui/material/Skeleton'

// Types
import type { ThemeColor } from '@core/types'
import type { KPICardsProps } from '@/types/dashboards/resumeTypes'

// Components
import IconifyIcon from '@/components/layout/shared/Icons'
import CustomAvatar from '@core/components/mui/Avatar'

// Hooks
import { useDashboardResumeTranslations } from '@/hooks/useTranslations'

// Types para componente interno
interface KPICardProps {
  title: string
  mainValue: string
  subLabel: string
  subValue: string
  icon: string
  color: ThemeColor
  loading?: boolean
}

// Componente individual de KPI
const KPICard = ({
  title,
  mainValue,
  subLabel,
  subValue,
  icon,
  color,
  loading = false
}: KPICardProps) => {
  if (loading) {
    return (
      <Card sx={{ width: '100%', borderLeft: 4, borderColor: `${color}.main` }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width={120} height={20} />
              <Skeleton variant="rounded" width={36} height={36} />
            </Box>
            <Skeleton variant="text" width="80%" height={36} />
            <Skeleton variant="text" width="60%" height={22} />
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      sx={{
        width: '100%',
        borderLeft: 4,
        borderColor: `${color}.main`,
        transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 'var(--mui-customShadows-xl)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack spacing={1}>
          {/* Header: título + ícone */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            <CustomAvatar color={color} skin='light' variant='rounded' size={36}>
              <IconifyIcon icon={icon} width={22} height={22} />
            </CustomAvatar>
          </Box>

          {/* Valor principal */}
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            {mainValue}
          </Typography>

          {/* Divider */}
          <Divider sx={{ opacity: 0.6 }} />

          {/* Sub-informação */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {subLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {subValue}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

// Função para formatar valores monetários completos (sem abreviação, com centavos)
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

// Função para formatar percentual
const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`
}

const KPICards = ({ data, loading = false }: KPICardsProps) => {
  const { t } = useDashboardResumeTranslations()

  // 4 KPI cards com informação adicional contextual
  const kpis: KPICardProps[] = [
    // 1. Entradas do mês atual — principal: recebido, sub: a receber
    {
      title: t('cards.totalRevenue'),
      mainValue: formatCurrency(data?.monthEntries?.actual?.value ?? 0),
      subLabel: t('cards.receivable'),
      subValue: formatCurrency(data?.monthEntries?.forecast?.value ?? 0),
      icon: 'tabler:chart-line',
      color: 'success'
    },
    // 2. Saídas do mês atual — principal: pago, sub: a pagar
    {
      title: t('cards.totalExpenses'),
      mainValue: formatCurrency(data?.monthExits?.actual?.value ?? 0),
      subLabel: t('cards.payable'),
      subValue: formatCurrency(data?.monthExits?.forecast?.value ?? 0),
      icon: 'tabler:receipt',
      color: 'error'
    },
    // 3. Sobra de caixa do mês atual — principal: atual, sub: previsto
    {
      title: t('cards.netProfit'),
      mainValue: formatCurrency(data?.cashSurplus?.actual?.value ?? 0),
      subLabel: t('cards.forecast'),
      subValue: formatCurrency(data?.cashSurplus?.forecast?.value ?? 0),
      icon: 'tabler:cash',
      color: (data?.cashSurplus?.actual?.value ?? 0) >= 0 ? 'success' : 'error'
    },
    // 4. Contas a pagar mês atual — principal: pendingExpenses, sub: margem de lucro
    {
      title: t('cards.profitMarginCard'),
      mainValue: formatCurrency(data?.pendingExpenses?.value ?? 0),
      subLabel: t('cards.profitMargin'),
      subValue: formatPercentage(data?.profitMargin?.actual?.value ?? 0),
      icon: 'tabler:alert-triangle',
      color: 'warning'
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          '& > *': {
            flex: 1
          }
        }}
      >
        {kpis.map((kpi, index) => (
          <KPICard
            key={index}
            {...kpi}
            loading={loading}
          />
        ))}
      </Stack>
    </Box>
  )
}

export default KPICards
